/** Utilidades de notación A1 (columnas, rangos). Sin dependencias. */

export function colToIndex(col: string): number {
  let n = 0;
  for (const ch of col.toUpperCase()) {
    const code = ch.charCodeAt(0);
    if (code < 65 || code > 90) throw new Error(`Columna inválida: ${col}`);
    n = n * 26 + (code - 64);
  }
  return n - 1;
}

export function indexToCol(index: number): string {
  if (!Number.isInteger(index) || index < 0) throw new Error(`Índice de columna inválido: ${index}`);
  let s = '';
  let n = index + 1;
  while (n > 0) {
    const r = (n - 1) % 26;
    s = String.fromCharCode(65 + r) + s;
    n = Math.floor((n - 1) / 26);
  }
  return s;
}

/** Expande "A-M" o "A" a la lista de columnas. */
export function colRange(from: string, to: string = from): string[] {
  const a = colToIndex(from);
  const b = colToIndex(to);
  const out: string[] = [];
  for (let i = a; i <= b; i++) out.push(indexToCol(i));
  return out;
}

export function quoteSheet(sheet: string): string {
  return /^[A-Za-z0-9_]+$/.test(sheet) ? sheet : `'${sheet.replace(/'/g, "''")}'`;
}

export function a1(sheet: string, from: string, to?: string): string {
  return to ? `${quoteSheet(sheet)}!${from}:${to}` : `${quoteSheet(sheet)}!${from}`;
}

export interface ParsedRange {
  sheet: string;
  /** Índices 0-based de columna; fila 1-based (como en la hoja). */
  startCol: number;
  startRow: number;
  endCol: number;
  endRow: number;
}

/** Parsea "'OT-01'!A4:U204" o "Listas!C7". Requiere filas explícitas. */
export function parseA1(range: string): ParsedRange {
  const m = /^(?:'((?:[^']|'')+)'|([^!]+))!([A-Z]+)(\d+)(?::([A-Z]+)(\d+))?$/.exec(range.trim());
  if (!m) throw new Error(`Rango A1 no soportado: ${range}`);
  const sheet = (m[1] ?? m[2] ?? '').replace(/''/g, "'");
  const startCol = colToIndex(m[3]!);
  const startRow = Number(m[4]);
  const endCol = m[5] ? colToIndex(m[5]) : startCol;
  const endRow = m[6] ? Number(m[6]) : startRow;
  if (endCol < startCol || endRow < startRow) throw new Error(`Rango invertido: ${range}`);
  return { sheet, startCol, startRow, endCol, endRow };
}

/** Agrupa columnas (letras) en bloques contiguos: [A,B,C,E] → [[A,C],[E,E]]. */
export function contiguousBlocks(cols: string[]): Array<[string, string]> {
  const idx = [...new Set(cols.map(colToIndex))].sort((x, y) => x - y);
  const blocks: Array<[string, string]> = [];
  let start = idx[0];
  let prev = idx[0];
  for (let i = 1; i <= idx.length; i++) {
    const cur = idx[i];
    if (cur === undefined || cur !== prev! + 1) {
      if (start !== undefined) blocks.push([indexToCol(start), indexToCol(prev!)]);
      start = cur;
    }
    prev = cur;
  }
  return blocks;
}
